let activeDesktopAudio: HTMLAudioElement | null = null;

interface SpeakDesktopAssistantReplyInput {
  text: string;
  onStart?: () => void;
  onEnd?: () => void;
  onError?: (message: string) => void;
}

export async function speakDesktopAssistantReply({
  text,
  onStart,
  onEnd,
  onError,
}: SpeakDesktopAssistantReplyInput): Promise<boolean> {
  if (typeof window === "undefined") return false;
  const synthesize = window.electron?.voiceTts?.synthesize;
  if (!synthesize) return false;

  let result;
  try {
    result = await synthesize(text);
  } catch (error) {
    onError?.(error instanceof Error ? error.message : String(error));
    return false;
  }
  if (!result.ok || !result.audioUrl) {
    onError?.(result.error || "Local British voice unavailable");
    return false;
  }

  stopDesktopAssistantReply();
  return playDesktopAudio({
    audioUrl: result.audioUrl,
    onStart,
    onEnd,
    onError,
  });
}

export function stopDesktopAssistantReply(): void {
  if (!activeDesktopAudio) return;
  activeDesktopAudio.pause();
  activeDesktopAudio.src = "";
  activeDesktopAudio = null;
}

function playDesktopAudio({
  audioUrl,
  onStart,
  onEnd,
  onError,
}: {
  audioUrl: string;
  onStart?: () => void;
  onEnd?: () => void;
  onError?: (message: string) => void;
}): Promise<boolean> {
  return new Promise((resolve) => {
    const audio = new Audio(audioUrl);
    activeDesktopAudio = audio;
    let didStart = false;
    let didResolve = false;

    const finish = (ok: boolean) => {
      if (didResolve) return;
      didResolve = true;
      if (activeDesktopAudio === audio) activeDesktopAudio = null;
      onEnd?.();
      resolve(ok);
    };

    audio.onplaying = () => {
      didStart = true;
      onStart?.();
    };
    audio.onended = () => finish(true);
    audio.onerror = () => {
      onError?.("Local British voice playback failed");
      finish(didStart);
    };

    audio.play().catch((error) => {
      onError?.(error instanceof Error ? error.message : String(error));
      finish(false);
    });
  });
}
